import React, { useMemo, useState } from 'react';

import { Dropdown } from '@/components/chariot-ui';
import { Container } from '@/components/ui';
import { EvidenceFile } from '@/hooks/useEvidenceFiles';
import { AssetStatusComponent } from '@/sections/asset/components/badges';
import { POE, POEProps } from '@/sections/POE';
import { Webpage } from '@/types';
import type { DropdownOption } from '@/types/dropdown.types';
import { cn } from '@/utils/classname';
import { formatDate } from '@/utils/date.util';

const Item = ({ selectedFile }: { selectedFile: EvidenceFile | undefined }) => {
  return (
    <div className="bg-chariot-background-secondary flex w-full items-center gap-3 p-4">
      <div className="flex flex-col text-left">
        <span className="text-chariot-text-primary text-sm font-semibold">
          {selectedFile?.webpage?.url}
        </span>
        <span className="text-chariot-text-secondary text-xs">
          Content Length: {selectedFile?.webpage?.metadata.content_length}
        </span>
        <span className="text-chariot-text-secondary text-xs">
          {formatDate(selectedFile?.webpage?.created || '')}
        </span>
      </div>
      <AssetStatusComponent
        className="ml-auto"
        asset={selectedFile?.webpage as Webpage}
      />
    </div>
  );
};

function getEvidenceLabel(file: EvidenceFile, riskName: string): string {
  // Git secrets: use org/repo from the repository node
  if (riskName.startsWith('git-secret-') && file.asset) {
    const { org, name } = file.asset;
    return org ? `${org}/${name}` : name;
  }

  // Web risks via port: extract dns and IP from port.source (#asset#dns#ip)
  if (file.port?.source) {
    const parts = file.port.source.replace(/^#asset#/, '').split('#');
    const [dns, ip] = parts;
    const portLabel = `${dns}:${file.port.port}`;
    return ip && ip !== dns ? `${portLabel} (${ip})` : portLabel;
  }

  // Web risks via asset: use dns and IP
  if (file.asset?.dns) {
    const { dns, name } = file.asset;
    return name !== dns ? `${dns} (${name})` : dns;
  }

  // Default: original behavior
  return file?.webpage?.url || file.attachmentPath;
}

/**
 * web-secrets risks can have multiple evidence files.
 * This component allows the user to select which evidence file to use.
 */
export const POESelector: React.FC<
  POEProps & { evidenceFiles: EvidenceFile[] }
> = props => {
  const { evidenceFiles } = props;
  const evidenceFilesOptions = useMemo(
    () =>
      (evidenceFiles || []).map(file => ({
        label: getEvidenceLabel(file, props.name),
        value: file.attachmentPath,
      })),
    [evidenceFiles, props.name]
  );

  const [selectedFileOption, setSelectedFileOption] = useState<
    DropdownOption | undefined
  >(evidenceFilesOptions[0]);

  const defaultOption = evidenceFilesOptions[0];
  if (
    defaultOption &&
    selectedFileOption?.value !== defaultOption.value &&
    !evidenceFilesOptions.some(o => o.value === selectedFileOption?.value)
  ) {
    setSelectedFileOption(defaultOption);
  }

  const selectedFile = useMemo(() => {
    return evidenceFiles?.find(
      file => file.attachmentPath === selectedFileOption?.value
    );
  }, [evidenceFiles, selectedFileOption]);

  const poeLocation =
    selectedFile?.attachmentPath.split('proofs/')[1] || props.poeLocation;

  const multipleFiles = evidenceFiles && evidenceFiles.length > 1;

  return (
    <div className="mb-6">
      {multipleFiles && (
        <div className="mb-4 flex min-w-0 items-center gap-2">
          <h2 className="shrink-0 text-sm font-semibold">Evidence files:</h2>
          <Dropdown
            size="xs"
            options={evidenceFilesOptions}
            selected={selectedFileOption ? [selectedFileOption] : undefined}
            onSelect={option => {
              if (option) {
                setSelectedFileOption(option);
              }
            }}
          />

          <p className="text-chariot-text-secondary ml-auto text-sm">
            {evidenceFiles.length} Evidence file
            {evidenceFiles.length !== 1 ? 's' : ''} available
          </p>
        </div>
      )}
      <Container
        className={cn('size-full overflow-auto p-0')}
        tabIndex={-1}
        id={poeLocation}
        data-testid="evidence-content"
      >
        {multipleFiles && selectedFile && selectedFile?.webpage && (
          <Item selectedFile={selectedFile} />
        )}
        <POE
          {...props}
          key={poeLocation}
          poeLocation={poeLocation}
          webpage={evidenceFiles?.[0]?.webpage || selectedFile?.webpage}
        />
      </Container>
    </div>
  );
};
